'use client';

import { useMemo } from 'react';
import config from 'config';
import { usePathname, useRouter } from 'next/navigation';

import {
  Box,
  Flex,
  Tab,
  TabList,
  Tabs as ChakraTabs,
  useTheme
} from '@chakra-ui/react';

import { useDashboardSectionAccess } from 'hooks';

import {
  filterDashboardNavItems,
  getActiveDashboardNavTabIndex,
  isDashboardNavLinkActive
} from 'utils/helpers/dashboardNav';

import { TabType } from 'types';

const {
  dashboard: { tabs }
} = config;

const Tabs = () => {
  const {
    colors: {
      palette: {
        components: {
          ui: { tabs: styles }
        }
      }
    }
  } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const { isSectionEnabled } = useDashboardSectionAccess();

  const visibleTabs = useMemo(
    () => filterDashboardNavItems<TabType>(tabs, isSectionEnabled),
    [isSectionEnabled]
  );

  const tabIndex = useMemo(
    () => getActiveDashboardNavTabIndex(pathname, visibleTabs),
    [pathname, visibleTabs]
  );

  // A full tab set fills the bar nicely with `space-between`. Once sys-modules
  // hides enough sections, spreading the few remaining tabs edge-to-edge looks
  // sparse, so group them in the centre with a comfortable gap instead.
  const isCompactNav = visibleTabs.length <= 5;
  // Past eight tabs the default padding no longer fits between the 1001px desktop
  // breakpoint and ~1040px, nor once it grows to 30px at 2xl. `space-between` hands the
  // spare room back as gaps, so trimming the padding costs nothing at wider widths.
  const isDenseNav = visibleTabs.length > 8;

  return (
    <ChakraTabs
      position='relative'
      variant='unstyled'
      index={tabIndex}
      bg={styles.tabBackground}
      _after={{
        content: '""',
        position: 'absolute',
        display: 'block',
        height: '100%',
        width: '100vw',
        bg: styles.tabBackground,
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: -1
      }}
      _before={{
        content: '""',
        position: 'absolute',
        display: 'block',
        height: '1px',
        width: '100vw',
        bg: styles.bottomColor,
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        opacity: 0.5,
        zIndex: 1
      }}
    >
      <Flex justifyContent='center'>
        <TabList
          flexGrow='1'
          justifyContent={isCompactNav ? 'center' : 'space-between'}
          columnGap={
            isCompactNav
              ? { base: '12px', md: '28px', lg: '48px', xl: '64px' }
              : 0
          }
          flexWrap={isCompactNav ? 'wrap' : 'nowrap'}
          rowGap={isCompactNav ? '8px' : 0}
          py='20px'
        >
          {visibleTabs.map(({ link, label }: TabType) => (
            <Tab
              key={label}
              onClick={() => router.push(link)}
              sx={{
                position: 'relative',
                textTransform: 'uppercase',
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: isDashboardNavLinkActive(pathname, link)
                  ? 700
                  : 500,
                padding: 0,
                whiteSpace: 'nowrap',
                _after: {
                  content: '""',
                  position: 'absolute',
                  display: 'block',
                  h: '2px',
                  w: '100%',
                  bottom: '-5px',
                  bg: isDashboardNavLinkActive(pathname, link)
                    ? styles.tabSelectedAfterBg
                    : 'transparent',
                  borderRadius: '5px'
                },
                color: styles.tabColor
              }}
            >
              <Box
                px={
                  isDenseNav
                    ? { base: 0, md: '4px', xl: '12px' }
                    : { base: 0, md: '10px', xl: '20px', '2xl': '30px' }
                }
                _after={{
                  display: 'block',
                  content: `"${label}"`,
                  fontWeight: 'bold',
                  opacity: 0,
                  height: 0,
                  textAlign: 'center',
                  overflow: 'hidden',
                  visibility: 'hidden'
                }}
              >
                {label}
              </Box>
            </Tab>
          ))}
        </TabList>
      </Flex>
    </ChakraTabs>
  );
};

export default Tabs;
